/**
 * Helpers that keep a time entry's description in sync with its tags.
 *
 * Two rules are applied across the add/edit/quick/timer forms:
 *  1. On submit, an empty description is filled with the selected tag names.
 *  2. While editing, if the tag selection changes and the description is still
 *     exactly the old tag names, it is replaced with the new tag names. A
 *     description the user typed themselves is never touched.
 */

const TAG_SEPARATOR = ', ';

/**
 * Joined display names for the given tag ids, e.g. "Design, QA".
 * Falls back to the id when the lookup has no name for it (same behaviour the
 * forms had inline before).
 */
export const buildTagDescription = (tagIds, tagLookup) => {
  if (!Array.isArray(tagIds) || tagIds.length === 0) return '';
  const lookup = tagLookup || {};
  return tagIds
    .map((tagId) => {
      const key = String(tagId);
      return lookup[key] || lookup[tagId] || key;
    })
    .filter((name) => name && String(name).trim() !== '')
    .join(TAG_SEPARATOR);
};

// Comma-separated pieces, trimmed, lowercased and sorted so the comparison is
// order- and case-insensitive: "QA, Design" still matches tags [Design, QA].
const normalizeParts = (text) =>
  String(text == null ? '' : text)
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part !== '')
    .sort();

/**
 * True when `description` is exactly the names of `tagIds` (any order, any case),
 * i.e. it was auto-filled from those tags rather than written by the user.
 */
export const isDescriptionFromTags = (description, tagIds, tagLookup) => {
  const descriptionParts = normalizeParts(description);
  if (descriptionParts.length === 0) return false;
  const tagParts = normalizeParts(buildTagDescription(tagIds, tagLookup));
  if (tagParts.length === 0) return false;
  return (
    descriptionParts.length === tagParts.length &&
    descriptionParts.every((part, index) => part === tagParts[index])
  );
};

/**
 * Description to use after the tag selection changed from `previousTagIds` to
 * `nextTagIds`. Returns the description unchanged unless it was derived from the
 * previous tags; clearing every tag clears the tag-derived description too.
 */
export const syncDescriptionWithTags = ({ description, previousTagIds, nextTagIds, tagLookup }) => {
  if (!isDescriptionFromTags(description, previousTagIds, tagLookup)) return description;
  return buildTagDescription(nextTagIds, tagLookup);
};

/**
 * Tag names offered in the description suggestion dropdown, most useful first:
 * the combined string the auto-fill would produce (when several tags are picked),
 * then each selected tag, then the teamspace's remaining tags alphabetically.
 *
 * Unlike buildTagDescription this never falls back to a raw id — an id is not a
 * description anyone would want to pick.
 */
export const buildTagSuggestions = ({ selectedTagIds, tagLookup, limit = 20 }) => {
  const lookup = tagLookup || {};
  const seen = new Set();
  const suggestions = [];
  const push = (name) => {
    const value = String(name == null ? '' : name).trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    suggestions.push(value);
  };

  const selectedIds = Array.isArray(selectedTagIds) ? selectedTagIds : [];
  if (selectedIds.length > 1) push(buildTagDescription(selectedIds, lookup));
  selectedIds.forEach((tagId) => push(lookup[String(tagId)] || lookup[tagId]));
  Object.values(lookup)
    .slice()
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach(push);

  return suggestions.slice(0, limit);
};
